import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, X, ArrowRight, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { proxyImg } from '@/lib/imageProxy';
import { getWishlist, toggleWish, subscribe, WISH_KEY_NAME } from '@/lib/wishlist';
import { useCart } from '@/lib/cart';

export default function Wishlist() {
    const [ids, setIds] = useState(getWishlist());
    const [products, setProducts] = useState([]);
    const { add: addToCart } = useCart();

    useEffect(() => subscribe(WISH_KEY_NAME, () => setIds(getWishlist())), []);
    useEffect(() => {
        if (ids.length === 0) { setProducts([]); return; }
        Promise.all(ids.map((id) => api.get(`/catalog/products/${id}`).then(({ data }) => data.product || data).catch(() => null)))
            .then((items) => setProducts(items.filter(Boolean)));
    }, [ids]);

    if (ids.length === 0) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="wishlist-empty">
                <Heart size={42} strokeWidth={1.2} className="text-rose-500 mb-4" />
                <h2 className="font-serif text-3xl text-inorbit-blue mb-2">Your wishlist is empty</h2>
                <p className="text-text-secondary max-w-md mb-6">Tap the <strong>heart</strong> on any product to save it for later.</p>
                <Link to="/shop" className="btn-primary">Discover the catalogue <ArrowRight size={14} /></Link>
            </div>
        );
    }

    return (
        <div className="bg-cream min-h-screen" data-testid="wishlist-page">
            <section className="bg-inorbit-blue-deeper text-white pt-28 pb-10">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="overline text-inorbit-yellow mb-2">Saved for later</div>
                    <h1 className="font-serif text-4xl md:text-5xl" style={{ color: 'white' }}>My wishlist</h1>
                    <p className="text-white/75 text-sm mt-2">{products.length} saved items</p>
                </div>
            </section>
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {products.map((p) => (
                    <div key={p.id} className="card-luxury group relative" data-testid={`wishlist-card-${p.id}`}>
                        <button onClick={() => toggleWish(p.id)} className="absolute top-3 right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow z-10 text-rose-500 hover:bg-rose-100" aria-label="Remove">
                            <X size={14} />
                        </button>
                        <Link to={`/product/${p.id}`} className="block">
                            <div className="aspect-[4/5] overflow-hidden bg-cream">
                                <img src={proxyImg(p.image || p.images?.[0])} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                            </div>
                            <div className="p-4">
                                <div className="overline text-inorbit-orange text-[10px] mb-1 truncate">{p.brand}</div>
                                <div className="text-sm text-inorbit-blue line-clamp-2 mb-2">{p.name}</div>
                                <div className="flex items-baseline gap-2">
                                    <div className="font-semibold text-inorbit-blue">₹{Number(p.price).toLocaleString('en-IN')}</div>
                                    {p.mrp > p.price && <div className="text-xs line-through text-text-tertiary">₹{Number(p.mrp).toLocaleString('en-IN')}</div>}
                                </div>
                            </div>
                        </Link>
                        <div className="px-4 pb-4">
                            <button onClick={async () => { try { await addToCart(p.id, 1); toast.success('Added to bag'); } catch { toast.error('Could not add'); } }} className="btn-outline border-inorbit-blue text-inorbit-blue hover:bg-inorbit-blue hover:text-white w-full justify-center" data-testid={`wish-add-${p.id}`}>
                                <ShoppingBag size={12} /> Add to bag
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
